import { create } from "lodash";
import { handleApiError } from "utils/errorHandler";
import { getAuthCookie } from "../../utils/authCookies";
import { forcePermissionsFalse, resolveSellerScopeOrganizationId } from "api/salesOrderApi";

const API = process.env.REACT_APP_API_URL as string;

const jsonHeaders = (token: string) => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
});


export async function getActiveTaxType(token: string, organizationId: string): Promise<"manual" | "taxcloud" | "geera" | string> {
    const orgId = String(organizationId ?? resolveSellerScopeOrganizationId() ?? "").trim();
    if (!orgId) return "greera";
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${process.env.REACT_APP_API_URL}${forcePermissionsFalse(`/organizations/${orgId}/tax/active-type`)}`;
    
    try {
        // console.log("getActiveTaxType1", userId, personId)
        const res = await fetch(url, {
            method: "GET",
            headers: jsonHeaders(token),
        });
        const data = await res.json();
        // console.log(data, "dataactive")
        return String(data?.taxType).toLowerCase();
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandremovalsmanualTax_get_active_tax_type",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url,
            method: "GET",
            additionalInfo: {
                organizationId: orgId,
            },
        }, token);
        return "manual"; // fallback
    }
}


// manualtax.tsx

// ---------- shared helpers ----------
type Dest =
    | {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        country?: string;
        zip?: string;
        cityCode?: string;
        stateCode?: string;
        countryCode?: string;
    }
    | null
    | undefined;

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const toManualJobDest = (a: any) => {
    const src = a?.address_details || a || {};
    return {
        addressLine1: String(src.addressLine1 || src.line1 || src.address1 || ""),
        addressLine2: String(src.addressLine2 || src.line2 || src.address2 || ""),
        city: String(src.city || src.cityCode || ""),
        state: String(src.state || src.stateCode || ""),
        country: String(src.country || src.countryCode || "US"),
        zip: String(src.zip || src.postalCode || src.postcode || ""),
        stateCode: String(src.stateCode || src.state || ""),
        countryCode: String(src.countryCode || src.country || "US"),
    };
};

// ---------- INSTALLATION (manual) ----------
/**
 * POST /signage/:createdById/jobs/calculate-tax
 * body {
 *  created_by_id, dynamic_cost_id?, job_types:["installation"],
 *  subtotal, destination_address
 * }
 * returns { data: { subtotal, tax, grand_total, tax_rate ... } }
 */
export async function postInstallationManualTax(
    token: string,
    createdById: string,
    args: {
        installation_cost: number;
        destination: any;
        dynamic_cost_id?: string | null;   // backend requires this
        dynamicCostId?: string | null;     // allow camelCase too
        job_types?: string[];              // optional
        supplierId?: string;
    }
): Promise<{
    installation_subtotal: number;
    installation_tax_amount: number;
    grand_total: number;
    tax_rate?: number;
    matched?: boolean;
}> {
    const API = process.env.REACT_APP_API_URL || "";
    const subtotal = Number(args?.installation_cost || 0);

    // normalize dest + dynamic cost id + job types
    const destination_address = toManualJobDest(args?.destination);
    const dynId = (args?.dynamic_cost_id ?? args?.dynamicCostId) ? String(args.dynamic_cost_id ?? args.dynamicCostId) : "";
    const jobTypes = Array.isArray(args?.job_types) && args!.job_types!.length > 0 ? args!.job_types! : ["installation"];
    const supplierId = args?.supplierId;

    // 🔐 hard guard: backend *requires* dynamic_cost_id
    if (!dynId) {
        console.warn("[postInstallationManualTax] Missing dynamic_cost_id, server will reject.");
    }

    const body = {
        // created_by_id: String(createdById || ""),
        created_by_id: supplierId,
        job_types: jobTypes,
        subtotal,
        destination_address,
        dynamic_cost_id: dynId,            // ✅ ALWAYS set from args
    };

    // console.log("body123445", body);     // should print dynamic_cost_id: "<uuid>"

    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}/signage/${supplierId}/jobs/calculate-tax`;
    
    try {
        const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });

        if (!res.ok) {
            // graceful fallback
            return {
                installation_subtotal: r2(subtotal),
                installation_tax_amount: 0,
                grand_total: r2(subtotal),
            };
        }

        const j = await res.json().catch(() => ({}));
        const d = j?.data || j || {};
        return {
            installation_subtotal: r2(Number(d?.subtotal || subtotal || 0)),
            installation_tax_amount: r2(Number(d?.tax || 0)),
            grand_total: r2(Number(d?.grand_total || (subtotal + Number(d?.tax || 0)))),
            tax_rate: Number(d?.tax_rate ?? 0),
            matched: Boolean(d?.matched),
        };
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandremovalsmanualTax_post_installation_manual_tax",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url,
            method: "POST",
            requestBody: body,
            additionalInfo: {
                supplierId,
                createdById,
                dynamic_cost_id: dynId,
            },
        }, token);
        // graceful fallback on error
        return {
            installation_subtotal: r2(subtotal),
            installation_tax_amount: 0,
            grand_total: r2(subtotal),
        };
    }
}

// export async function postInstallationManualTax(
//   token: string,
//   createdById: string,
//   args: {
//     installation_cost: number;
//     destination: Dest;
//     job_types?: string[];
//     // optional: if you have a dynamic cost id from your cost endpoint
//     dynamic_cost_id?: string | null;
//   }
// ): Promise<{
//   installation_subtotal: number;
//   installation_tax_amount: number;
//   grand_total: number;
//   tax_rate?: number;
//   matched?: boolean;
// }> {
//   const API = process.env.REACT_APP_API_URL || "";
//   const subtotal = Number(args?.installation_cost || 0);
//   console.log("subtotal", subtotal);
//   const destination_address = toManualJobDest(args?.destination);
//  const dynamic_cost_id = String(args?.dynamic_cost_id);
//   const body: any = {
//     created_by_id: String(createdById || ""),
//     job_types:args?.job_types,
//     subtotal,
//     destination_address,
//    dynamic_cost_id
//   };
//   console.log("body123445", body);
//   // if (args?.dynamic_cost_id) body.dynamic_cost_id = String(args.dynamic_cost_id);

//   const url = `${API}/signage/${createdById}/jobs/calculate-tax`;
//   const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });
//   if (!res.ok) {
//     // graceful fallback
//     return {
//       installation_subtotal: r2(subtotal),
//       installation_tax_amount: 0,
//       grand_total: r2(subtotal),
//     };
//   }

//   const j = await res.json().catch(() => ({}));
//   const d = j?.data || j || {};
//   return {
//     installation_subtotal: r2(Number(d?.subtotal || subtotal || 0)),
//     installation_tax_amount: r2(Number(d?.tax || 0)),
//     grand_total: r2(Number(d?.grand_total || (subtotal + Number(d?.tax || 0)))),
//     tax_rate: Number(d?.tax_rate ?? 0),
//     matched: Boolean(d?.matched),
//   };
// }

// ---------- SPECIAL FEES (manual, multi) ----------
/**
 * POST /special-fees/calculate-tax/created-by/:id
 * body { destination_address, special_fees: [full fee objects] }
 * server returns per-fee rows with amount + tax; we sum them.
 */
export async function postSpecialFeeManualTax(
    token: string,
    createdById: string,
    args: {
        destination: Dest;
        fees: Array<{
            id: string | number;
            name: string;
            amount: number;
            taxable?: boolean;
            shippingId?: string,
            tax_percentage?: number | null;
            tax_rule_id: string | null;
            tax_group_id: string | null;
        }>;
    }
): Promise<{
    specialfee_subtotal: number;
    specialfee_tax_amount: number;
    rows: Array<{
        specialfee_id: string;
        specialfee_name: string;
        specialfee_amount: number;
        specialfee_tax_percentage: number; // 0–100
        specialfee_tax_amount: number;
        specialfee_taxable: boolean;
    }>;
}> {
    const API = process.env.REACT_APP_API_URL || "";
    const destination_address = toManualJobDest(args?.destination);
    const body = {
        destination_address,
        special_fees: (args?.fees || []).map((f) => ({
            id: String(f.id ?? ""),
            name: String(f.name ?? ""),
            amount: Number(f.amount || 0),
            taxable: f.taxable !== false,
            tax_percentage: f.tax_percentage != null ? Number(f.tax_percentage) : null,
            tax_rule_id: String(f.tax_rule_id ?? ""),
            tax_group_id: String(f.tax_group_id ?? ""),
        })),
    };

    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}/special-fees/calculate-tax/organizations/${createdById}`;
    
    // default empty/zero
    const zeroRows = (args?.fees || []).map((f, i) => ({
        specialfee_id: String(f.id ?? `fee-${i}`),
        specialfee_name: String(f.name ?? `Special Fee ${i + 1}`),
        specialfee_amount: Number(f.amount || 0),
        specialfee_tax_percentage: 0,
        specialfee_tax_amount: 0,
        specialfee_taxable: f.taxable !== false,
    }));
    
    try {
        const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });

        if (!res.ok) {
            const fSub = zeroRows.reduce((s, r) => s + r.specialfee_amount, 0);
            return { specialfee_subtotal: r2(fSub), specialfee_tax_amount: 0, rows: zeroRows };
        }

        const data = await res.json().catch(() => []);
        const rows = (Array.isArray(data) ? data : []).map((r: any) => ({
            specialfee_id: String(r.specialfee_id ?? r.fee_id ?? r.id ?? ""),
            specialfee_name: String(r.specialfee_name ?? r.fee_name ?? r.name ?? ""),
            specialfee_amount: Number(r.specialfee_amount ?? r.fee_amount ?? r.amount ?? 0),
            specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? r.tax_rate ?? 0),
            specialfee_tax_amount: Number(r.specialfee_tax_amount ?? r.tax_amount ?? 0),
            specialfee_taxable: (r.tax_amount ?? 0) > 0 || (r.tax_rate ?? 0) > 0,
        }));

        const fSub = rows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0);
        const fTax = rows.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0);
        return { specialfee_subtotal: r2(fSub), specialfee_tax_amount: r2(fTax), rows };
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandremovalsmanualTax_post_special_fee_manual_tax",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url,
            method: "POST",
            requestBody: body,
            additionalInfo: {
                createdById,
                feeCount: (args?.fees || []).length,
            },
        }, token);
        // graceful fallback on error
        const fSub = zeroRows.reduce((s, r) => s + r.specialfee_amount, 0);
        return { specialfee_subtotal: r2(fSub), specialfee_tax_amount: 0, rows: zeroRows };
    }
}



export const SpecialFeeManualTax = postSpecialFeeManualTax;